import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { FormikProps } from "formik";
import type { SilaeFormData, SilaeCondition } from "@/types/silae";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Filter, Plus, Trash2, AlertCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "../ui/label";
import { RubriqueSelect } from "./RubriqueSelect";
import { FormikSelect, FormikInput } from "@/components/FormikField";
import { MemoSection } from "./MemoSection";
import { CounterFeedingConfig } from "./CounterFeedingConfig";
import { TauxSourceConfig } from "./TauxSourceConfig";
import { BaseSourceConfig } from "./BaseSourceConfig";
import { ConditionItem } from "./ConditionItem";
import { StockageConfig } from "./StockageConfig";
import { DsnPeriodicityConfig } from "./DsnPeriodicityConfig";
import { getIn } from "formik";

interface BonusConfigProps {
  formik: FormikProps<SilaeFormData>;
  bonusIndex: number;
  totalBonuses: number;
  branch?: "ifBranch" | "elseBranch"; // Prop pour spécifier la branche (IF ou ELSE)
}

/**
 * Composant pour configurer une seule prime
 * Support pour les branches IF et ELSE via le prop `branch`
 */
const BonusConfig: React.FC<BonusConfigProps> = ({
  formik,
  bonusIndex,
  totalBonuses,
  branch = "ifBranch", // Par défaut: branche IF
}) => {
  const { t } = useTranslation();
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [activeTab, setActiveTab] = useState("condition-0");

  const bonus = useMemo(
    () => formik.values[branch]?.bonuses[bonusIndex],
    [formik.values, branch, bonusIndex],
  );

  const fieldPrefix = `${branch}.bonuses.${bonusIndex}`;

  const handleLabelAutoFill = (label: string) => {
    // Toujours remplacer le libellé avec celui du code rubrique sélectionné
    formik.setFieldValue(`${fieldPrefix}.libelle`, label);
  };

  // Gestion des conditions de la prime
  const bonusConditions = useMemo(
    () => bonus?.conditions || [],
    [bonus?.conditions],
  );

  const hasConditions = useMemo(
    () => bonus?.hasConditions || false,
    [bonus?.hasConditions],
  );

  // Forcer les conditions si plusieurs primes
  const isConditionsRequired = totalBonuses > 1;
  const effectiveHasConditions = isConditionsRequired || hasConditions;

  // Récupérer les erreurs de validation
  const conditionsErrors = useMemo(
    () => getIn(formik.errors, `${fieldPrefix}.conditions`),
    [formik.errors, fieldPrefix],
  );

  const hasConditionsErrors =
    conditionsErrors &&
    (Array.isArray(conditionsErrors) ? conditionsErrors.length > 0 : true);

  const toggleBonusConditions = useCallback(
    (enabled: boolean) => {
      // Empêcher la désactivation si plusieurs primes
      if (isConditionsRequired && !enabled) {
        return;
      }

      formik.setFieldValue(`${fieldPrefix}.hasConditions`, enabled);
      if (!enabled) {
        formik.setFieldValue(`${fieldPrefix}.conditions`, []);
        formik.setFieldValue(`${fieldPrefix}.conditionsOperators`, []);
      } else {
        if (bonusConditions.length === 0) {
          const newCondition: SilaeCondition = {
            id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: "VAR_SALARY_BASE",
            silaeVariable: "bul.salairedebase",
            conditionOperator: ">=",
            conditionValue: "",
          };
          formik.setFieldValue(`${fieldPrefix}.conditions`, [newCondition]);
        }
      }
    },
    [formik, fieldPrefix, bonusConditions.length, isConditionsRequired],
  );

  const addBonusCondition = useCallback(() => {
    const newCondition: SilaeCondition = {
      id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: "VAR_SALARY_BASE",
      silaeVariable: "bul.salairedebase",
      conditionOperator: ">=",
      conditionValue: "",
      conditionPercentage: "100",
    };
    const updatedConditions = [...bonusConditions, newCondition];
    formik.setFieldValue(`${fieldPrefix}.conditions`, updatedConditions);
    setActiveTab(`condition-${updatedConditions.length - 1}`);
  }, [bonusConditions, formik, fieldPrefix]);

  const removeBonusCondition = useCallback(
    (index: number) => {
      if (bonusConditions.length <= 1) return;
      const updatedConditions = bonusConditions.filter((_, i) => i !== index);
      formik.setFieldValue(`${fieldPrefix}.conditions`, updatedConditions);

      // Ajuster les opérateurs si nécessaire
      const ops = (bonus?.conditionsOperators || []) as string[];
      if (ops.length > 0 && updatedConditions.length > 1) {
        const updatedOps: string[] = [];
        // On garde tous les opérateurs avant la condition supprimée
        for (let i = 0; i < index && i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        // On saute l'opérateur qui était après la condition supprimée (index)
        // et on garde tous les opérateurs suivants
        for (let i = index + 1; i < ops.length; i++) {
          updatedOps.push(ops[i] || "AND");
        }
        // Si on n'a plus assez d'opérateurs, on ajoute des AND par défaut
        while (updatedOps.length < updatedConditions.length - 1) {
          updatedOps.push("AND");
        }
        formik.setFieldValue(`${fieldPrefix}.conditionsOperators`, updatedOps);
      } else if (updatedConditions.length === 1) {
        // Si on n'a plus qu'une condition, on supprime tous les opérateurs
        formik.setFieldValue(`${fieldPrefix}.conditionsOperators`, []);
      }

      // Ajuster l'onglet actif
      if (activeTab === `condition-${index}`) {
        const newIndex = Math.max(0, index - 1);
        setActiveTab(`condition-${newIndex}`);
      } else {
        // Mettre à jour l'index de l'onglet actif si nécessaire
        const currentTabIndex = parseInt(activeTab.split("-")[1] || "0");
        if (currentTabIndex > index) {
          setActiveTab(`condition-${currentTabIndex - 1}`);
        }
      }
    },
    [
      bonusConditions,
      formik,
      fieldPrefix,
      bonus?.conditionsOperators,
      activeTab,
    ],
  );

  const calculMethodOptions = [
    {
      value: "1",
      label: t("step3Configuration.calculMethods.baseRate"),
      description: t("step3Configuration.calculMethodsDescriptions.baseRate"),
    },
    {
      value: "2",
      label: t("step3Configuration.calculMethods.fixed"),
      description: t("step3Configuration.calculMethodsDescriptions.fixed"),
    },
    {
      value: "3",
      label: t("step3Configuration.calculMethods.base20"),
      description: t("step3Configuration.calculMethodsDescriptions.base20"),
    },
    {
      value: "99",
      label: t("step3Configuration.calculMethods.counter"),
      description: t("step3Configuration.calculMethodsDescriptions.counter"),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Configuration des conditions spécifiques à la prime */}
      <Card
        className={`bg-background dark:bg-background ${
          hasConditionsErrors ? "border-red-500" : ""
        }`}
      >
        <CardHeader className="flex mb-0 items-center p-4 mx-6 rounded-lg bg-muted/50 justify-between">
          <div className="flex flex-col justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">
                {t("step3Configuration.bonusConditions.title")}
              </CardTitle>
            </div>
            <CardDescription>
              {isConditionsRequired
                ? t("step3Configuration.bonusConditions.requiredForMultiple")
                : t("step3Configuration.bonusConditions.description")}
            </CardDescription>
          </div>
          <Switch
            id={`hasBonusConditions-${bonusIndex}`}
            checked={effectiveHasConditions}
            onCheckedChange={toggleBonusConditions}
            disabled={isConditionsRequired}
          />
        </CardHeader>

        {effectiveHasConditions && (
          <CardContent className="space-y-4">
            {/* Affichage des erreurs générales de validation */}
            {hasConditionsErrors && (
              <div className="space-y-2">
                <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      {typeof conditionsErrors === "string" ? (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {conditionsErrors}
                        </p>
                      ) : Array.isArray(conditionsErrors) ? (
                        <div className="space-y-1">
                          {conditionsErrors.map(
                            (error: unknown, index: number) => {
                              if (!error) return null;
                              if (typeof error === "string") {
                                return (
                                  <p
                                    key={index}
                                    className="text-sm text-red-600 dark:text-red-400"
                                  >
                                    {t("advancedConditions.condition")}{" "}
                                    {index + 1}: {error}
                                  </p>
                                );
                              }
                              if (typeof error === "object") {
                                return (
                                  <div key={index} className="space-y-1">
                                    {(
                                      Object.entries(
                                        error as Record<string, unknown>,
                                      ) as [string, unknown][]
                                    ).map(([key, err]: [string, unknown]) => {
                                      if (!err) return null;
                                      return (
                                        <p
                                          key={key}
                                          className="text-sm text-red-600 dark:text-red-400"
                                        >
                                          {t("advancedConditions.condition")}{" "}
                                          {index + 1}.{key}: {String(err)}
                                        </p>
                                      );
                                    })}
                                  </div>
                                );
                              }
                              return null;
                            },
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {String(conditionsErrors)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Information sur les conditions multiples */}
            {bonusConditions.length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  💡 {t("step3Configuration.bonusConditions.multipleInfo")}
                </div>
              </div>
            )}

            {/* Opérateur logique entre conditions (si plusieurs) */}

            {/* Bouton pour ajouter une condition */}
            <div className="flex items-center justify-between gap-2">
              {bonusConditions.length > 1 && (
                <div className="space-y-2 border-b pb-4">
                  <Label>{t("advancedConditions.logicalOperator")}</Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    {t("advancedConditions.operatorBetweenConditions")}
                  </div>
                  {/* Afficher les opérateurs pour chaque paire de conditions */}
                  <div className="flex flex-wrap gap-4 items-center">
                    {bonusConditions.slice(0, -1).map((_, index) => {
                      const ops = (bonus?.conditionsOperators ||
                        []) as string[];
                      const current = ops[index] || "AND";

                      return (
                        <div key={index} className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {t("advancedConditions.condition")} {index + 1}
                          </Badge>
                          <Select
                            value={current}
                            onValueChange={(value) => {
                              const next = [...ops];
                              while (next.length < bonusConditions.length - 1)
                                next.push("AND");
                              next[index] = value;
                              formik.setFieldValue(
                                `${fieldPrefix}.conditionsOperators`,
                                next,
                              );
                            }}
                          >
                            <SelectTrigger className="w-fit min-w-[80px]">
                              <SelectValue>
                                {current === "AND" ? (
                                  <Badge variant="secondary">
                                    {t(
                                      "advancedConditions.operators.and.label",
                                    )}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">
                                    {t("advancedConditions.operators.or.label")}
                                  </Badge>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AND">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary">
                                    {t(
                                      "advancedConditions.operators.and.label",
                                    )}
                                  </Badge>
                                  <span>
                                    {t(
                                      "advancedConditions.operators.and.description",
                                    )}
                                  </span>
                                </div>
                              </SelectItem>
                              <SelectItem value="OR">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">
                                    {t("advancedConditions.operators.or.label")}
                                  </Badge>
                                  <span>
                                    {t(
                                      "advancedConditions.operators.or.description",
                                    )}
                                  </span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <Badge variant="outline" className="text-xs">
                            {t("advancedConditions.condition")} {index + 2}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={addBonusCondition}
                className="w-fit"
                disabled={bonusConditions.length >= 5}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("step3Configuration.bonusConditions.addCondition")}
              </Button>
            </div>

            {/* Liste des conditions avec système de tabs si plusieurs */}
            {bonusConditions.length === 1 ? (
              // Une seule condition : affichage direct sans tabs
              <ConditionItem
                formik={
                  {
                    ...formik,
                    values: {
                      ...formik.values,
                      conditions: bonusConditions,
                    },
                    errors: {
                      ...formik.errors,
                      conditions:
                        typeof formik.errors[branch] === "object" &&
                        formik.errors[branch]?.bonuses?.[bonusIndex] &&
                        typeof formik.errors[branch].bonuses[bonusIndex] ===
                          "object"
                          ? (
                              formik.errors[branch].bonuses[bonusIndex] as {
                                conditions?: unknown;
                              }
                            ).conditions
                          : undefined,
                    },
                    touched: {
                      ...formik.touched,
                      conditions:
                        typeof formik.touched[branch] === "object" &&
                        formik.touched[branch]?.bonuses?.[bonusIndex] &&
                        typeof formik.touched[branch].bonuses[bonusIndex] ===
                          "object"
                          ? (
                              formik.touched[branch].bonuses[bonusIndex] as {
                                conditions?: unknown;
                              }
                            ).conditions
                          : undefined,
                    },
                    setFieldValue: (field: string, value: unknown) => {
                      if (field.startsWith("conditions[")) {
                        const bonusField = field.replace(
                          "conditions[",
                          `${fieldPrefix}.conditions[`,
                        );
                        formik.setFieldValue(bonusField, value);
                      } else if (field.startsWith("conditions.")) {
                        const bonusField = field.replace(
                          "conditions.",
                          `${fieldPrefix}.conditions.`,
                        );
                        formik.setFieldValue(bonusField, value);
                      } else {
                        formik.setFieldValue(field, value);
                      }
                    },
                    setFieldTouched: (field: string, touched?: boolean) => {
                      if (field.startsWith("conditions[")) {
                        const bonusField = field.replace(
                          "conditions[",
                          `${fieldPrefix}.conditions[`,
                        );
                        formik.setFieldTouched(bonusField, touched);
                      } else if (field.startsWith("conditions.")) {
                        const bonusField = field.replace(
                          "conditions.",
                          `${fieldPrefix}.conditions.`,
                        );
                        formik.setFieldTouched(bonusField, touched);
                      } else {
                        formik.setFieldTouched(field, touched);
                      }
                    },
                  } as FormikProps<SilaeFormData>
                }
                conditionIndex={0}
                onDelete={() => removeBonusCondition(0)}
                canDelete={false}
              />
            ) : (
              // Plusieurs conditions : utiliser les tabs
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
                  {bonusConditions.map((condition, index) => (
                    <div key={condition.id} className="flex items-center gap-1">
                      <TabsTrigger
                        value={`condition-${index}`}
                        className="flex items-center gap-2"
                      >
                        <Filter className="h-3 w-3" />
                        {t("advancedConditions.condition")} {index + 1}
                        {bonusConditions.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeBonusCondition(index);
                            }}
                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-2 w-2" />
                          </Button>
                        )}
                      </TabsTrigger>
                    </div>
                  ))}
                </TabsList>

                {bonusConditions.map((_, index) => (
                  <TabsContent key={index} value={`condition-${index}`}>
                    <ConditionItem
                      formik={
                        {
                          ...formik,
                          values: {
                            ...formik.values,
                            conditions: bonusConditions,
                          },
                          errors: {
                            ...formik.errors,
                            conditions:
                              typeof formik.errors[branch] === "object" &&
                              formik.errors[branch]?.bonuses?.[bonusIndex] &&
                              typeof formik.errors[branch].bonuses[
                                bonusIndex
                              ] === "object"
                                ? (
                                    formik.errors[branch].bonuses[
                                      bonusIndex
                                    ] as {
                                      conditions?: unknown;
                                    }
                                  ).conditions
                                : undefined,
                          },
                          touched: {
                            ...formik.touched,
                            conditions:
                              typeof formik.touched[branch] === "object" &&
                              formik.touched[branch]?.bonuses?.[bonusIndex] &&
                              typeof formik.touched[branch].bonuses[
                                bonusIndex
                              ] === "object"
                                ? (
                                    formik.touched[branch].bonuses[
                                      bonusIndex
                                    ] as {
                                      conditions?: unknown;
                                    }
                                  ).conditions
                                : undefined,
                          },
                          setFieldValue: (field: string, value: unknown) => {
                            if (field.startsWith("conditions[")) {
                              const bonusField = field.replace(
                                "conditions[",
                                `${fieldPrefix}.conditions[`,
                              );
                              formik.setFieldValue(bonusField, value);
                            } else if (field.startsWith("conditions.")) {
                              const bonusField = field.replace(
                                "conditions.",
                                `${fieldPrefix}.conditions.`,
                              );
                              formik.setFieldValue(bonusField, value);
                            } else {
                              formik.setFieldValue(field, value);
                            }
                          },
                          setFieldTouched: (
                            field: string,
                            touched?: boolean,
                          ) => {
                            if (field.startsWith("conditions[")) {
                              const bonusField = field.replace(
                                "conditions[",
                                `${fieldPrefix}.conditions[`,
                              );
                              formik.setFieldTouched(bonusField, touched);
                            } else if (field.startsWith("conditions.")) {
                              const bonusField = field.replace(
                                "conditions.",
                                `${fieldPrefix}.conditions.`,
                              );
                              formik.setFieldTouched(bonusField, touched);
                            } else {
                              formik.setFieldTouched(field, touched);
                            }
                          },
                        } as FormikProps<SilaeFormData>
                      }
                      conditionIndex={index}
                      onDelete={() => removeBonusCondition(index)}
                      canDelete={bonusConditions.length > 1}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        )}
      </Card>

      <Card className="bg-background dark:bg-background mb-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t("step3Configuration.title")}
          </CardTitle>
          <CardDescription>
            {formik.values.structureType === "simple"
              ? t("step3Configuration.description.simple")
              : t("step3Configuration.description.conditional")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col lg:flex-row gap-4">
          <div className="space-y-2 flex-1">
            <Label>{t("step3Configuration.rubriqueCode")}</Label>
            <RubriqueSelect
              value={bonus?.rubriqueNumber || ""}
              onValueChange={(value) => {
                formik.setFieldValue(`${fieldPrefix}.rubriqueNumber`, value);
                formik.setFieldTouched(`${fieldPrefix}.rubriqueNumber`, true);
                requestAnimationFrame(() => {
                  formik.setFieldTouched(`${fieldPrefix}.rubriqueNumber`, true);
                });
              }}
              onLabelAutoFill={handleLabelAutoFill}
              isManualEntry={isManualEntry}
              onManualEntryChange={setIsManualEntry}
              placeholder={t("step3Configuration.selectRubrique")}
              error={
                typeof formik.errors[branch] === "object" &&
                formik.errors[branch]?.bonuses?.[bonusIndex] &&
                typeof formik.errors[branch].bonuses[bonusIndex] === "object"
                  ? formik.errors[branch].bonuses[bonusIndex].rubriqueNumber
                  : undefined
              }
              touched={
                typeof formik.touched[branch] === "object" &&
                formik.touched[branch]?.bonuses?.[bonusIndex] &&
                typeof formik.touched[branch].bonuses[bonusIndex] === "object"
                  ? formik.touched[branch].bonuses[bonusIndex].rubriqueNumber
                  : undefined
              }
              onBlur={(fieldName) => formik.setFieldTouched(fieldName, true)}
            />
          </div>

          {/* Méthode de calcul */}
          <FormikSelect
            formik={formik}
            name={`${fieldPrefix}.calculMethod`}
            label={t("step3Configuration.calculMethod")}
            options={calculMethodOptions}
            placeholder={t("step3Configuration.chooseMethod")}
            className="flex-1"
          />

          {/* Libellé */}
          <FormikInput
            formik={formik}
            name={`${fieldPrefix}.libelle`}
            label={t("step3Configuration.rubriqueLabel")}
            placeholder={t("step3Configuration.rubriquePlaceholder")}
            className="flex-1"
          />
        </CardContent>
      </Card>

      {/* Configuration de la prime */}
      <div className="flex gap-4">
        <BaseSourceConfig
          formik={formik}
          fieldPrefix={fieldPrefix}
          title={t("baseSourceConfig.title")}
          description={t("baseSourceConfig.description")}
          className="flex-1"
        />
        {(bonus?.calculMethod === "1" || bonus?.calculMethod === "3") && (
          <TauxSourceConfig
            formik={formik}
            fieldPrefix={fieldPrefix}
            title={t("tauxSourceConfig.title")}
            description={t("tauxSourceConfig.description")}
            className="flex-1"
          />
        )}
      </div>

      <div className="space-y-6 mb-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StockageConfig
          formik={formik}
          fieldPrefix={fieldPrefix}
          className=""
        />

        <CounterFeedingConfig
          formik={formik}
          fieldPrefix={fieldPrefix}
          className=""
        />
      </div>

      <DsnPeriodicityConfig
        formik={formik}
        fieldPrefix={fieldPrefix}
        className=""
      />

      <MemoSection
        formik={formik}
        fieldPrefix={fieldPrefix}
        title={t("memoSection.titleMain")}
      />
    </div>
  );
};

export default BonusConfig;
